import { useMemo } from "react";
import { useQueries, useQuery, type QueryClient } from "@tanstack/react-query";
import type { DateTime } from "luxon";

import { useDataApi } from "@core/apis";

import {
  getAverageSensorDataPerSubstationOptions,
  getSensorsDataPerSubstation,
  getSensorsPerSubstationOptions,
} from "../queries/resource.queries";
import type { Sensor, SensorDataPerSubstation, SensorTypeTechnicalName } from "../types";

/**
 * Fetch sensors for a substation filtered by one or more sensor type technical names.
 * Pass an empty array to fetch all sensors.
 */
export const useFilteredSensors = (
  substationUid: string,
  sensorTypes: string[],
  options?: { enabled?: boolean }
) => {
  const dataApi = useDataApi();
  return useQuery({
    // eslint-disable-next-line @tanstack/query/exhaustive-deps
    queryKey: ["sensors", "filtered", substationUid, sensorTypes],
    queryFn: () => dataApi!.getFilteredSensors(substationUid, sensorTypes),
    enabled: options?.enabled !== false && !!dataApi && !!substationUid,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });
};

const flattenSensors = (
  flexStations: any[] | undefined,
  sensorTypes?: SensorTypeTechnicalName[]
): Sensor[] => {
  const flattened = flexStations?.flatMap((fs: any) => fs?.sensors ?? []) ?? [];
  if (!sensorTypes || sensorTypes.length === 0) return flattened;
  return flattened.filter((s: Sensor) => sensorTypes.includes(s.sensor_type_technical_name));
};

export const patchCachedSubstationSensor = (
  queryClient: QueryClient,
  substationUid: string,
  sensorUid: string,
  patch: {
    apartment: string;
    enabled: boolean;
    floor: number | string;
    street_address: string;
  }
) => {
  queryClient.setQueryData(["sensors", substationUid], (old: unknown) => {
    if (!Array.isArray(old)) return old;
    return old.map((flexStation: { sensors?: Array<{ uid: string }> }) => ({
      ...flexStation,
      sensors: flexStation.sensors?.map((sensor) =>
        sensor.uid === sensorUid
          ? {
              ...sensor,
              apartment: patch.apartment,
              enabled: patch.enabled,
              floor: String(patch.floor),
              street_address: patch.street_address,
            }
          : sensor
      ),
    }));
  });
};

export const useSubstationSensors = (
  substationId: string,
  sensorTypes?: SensorTypeTechnicalName[]
) => {
  const dataApi = useDataApi();

  const { data: flexStations, isLoading } = useQuery({
    ...getSensorsPerSubstationOptions(dataApi, substationId),
    enabled: !!dataApi && !!substationId,
  });

  const sensors = useMemo(
    () => flattenSensors(flexStations, sensorTypes),
    [flexStations, sensorTypes]
  );

  return {
    sensors,
    isLoading,
    isEmpty: !isLoading && sensors.length === 0,
  };
};

export const useSensorReadings = (
  substationId: string,
  sensorUids: string[],
  dateRange: { startDate: DateTime; endDate: DateTime },
  options?: { enabled?: boolean }
) => {
  const dataApi = useDataApi();
  const enabled = options?.enabled !== false && sensorUids.length > 0 && !!substationId;

  return useQueries({
    queries: sensorUids.map((sensorUid) => ({
      ...getSensorsDataPerSubstation(
        dataApi,
        substationId,
        sensorUid,
        dateRange.startDate,
        dateRange.endDate
      ),
      enabled: enabled && !!sensorUid,
      staleTime: 30 * 60 * 1000,
      gcTime: 60 * 60 * 1000,
      refetchOnWindowFocus: false,
    })),
    combine: (queryResults) => {
      const data: Record<string, SensorDataPerSubstation[]> = {};
      let isAnyLoading = false;
      let hasAnyData = false;

      queryResults.forEach((result, index) => {
        const uid = sensorUids[index];
        if (result.isLoading) isAnyLoading = true;
        if (result.isSuccess && result.data) {
          data[uid] = result.data as SensorDataPerSubstation[];
          hasAnyData = true;
        } else {
          data[uid] = [];
        }
      });

      return {
        data: hasAnyData ? data : ({} as Record<string, SensorDataPerSubstation[]>),
        isLoading: isAnyLoading,
        isSuccess: queryResults.some((r) => r.isSuccess),
      };
    },
  });
};

export const useAverageSensorData = (
  substationId: string,
  sensorType: SensorTypeTechnicalName,
  dateRange: { startDate: DateTime; endDate: DateTime }
) => {
  const dataApi = useDataApi();

  const { data, isLoading } = useQuery({
    ...getAverageSensorDataPerSubstationOptions(
      dataApi,
      substationId,
      sensorType,
      dateRange.startDate,
      dateRange.endDate
    ),
    staleTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
  });

  return { data: data as SensorDataPerSubstation[] | undefined, isLoading };
};
